import { useMemo, useState, useEffect } from 'react'
import { useLanguage } from '../context/LanguageContext'
import { cn } from '../lib/utils'

// 5 levels of investment growth
const INVESTMENT_LEVELS = [
  { maxRatio: 0.03, level: 1, message: { es: 'Inicio temprano', en: 'Very early start' } },
  { maxRatio: 0.07, level: 2, message: { es: 'Buen inicio', en: 'Good start' } },
  { maxRatio: 0.12, level: 3, message: { es: 'Construyendo progreso', en: 'Making progress' } },
  { maxRatio: 0.20, level: 4, message: { es: 'Ahorro sustancial', en: 'Substantial savings' } },
  { maxRatio: Infinity, level: 5, message: { es: '¡Meta alcanzada!', en: 'Goal achieved!' } },
]

// Premium 3D Gold Coin with brushed matte finish
const GoldCoin = ({ size = 32, shadow = true }) => (
  <div 
    className="relative flex-shrink-0"
    style={{ 
      width: size, 
      height: size * 0.38,
    }}
  >
    {/* Coin edge - 3D depth with soft gradient */}
    <div 
      className="absolute inset-x-0 bottom-0 rounded-[50%]"
      style={{
        height: '100%',
        background: `linear-gradient(180deg, 
          #C9A962 0%, 
          #B8944A 25%,
          #A07D3A 50%, 
          #8A6B2E 75%,
          #755A25 100%
        )`,
        boxShadow: shadow 
          ? '0 3px 6px rgba(90,70,30,0.25), 0 1px 3px rgba(0,0,0,0.1)' 
          : 'none'
      }}
    />
    {/* Coin face - brushed matte gold with soft lighting from top-left */}
    <div 
      className="absolute inset-x-0 top-0 rounded-[50%]"
      style={{
        height: '68%',
        background: `linear-gradient(135deg, 
          #E8D9A8 0%,
          #DECE98 10%,
          #D4C088 25%, 
          #CBB67A 40%,
          #C2AC6C 55%,
          #B9A260 70%,
          #B09855 85%,
          #A78E4A 100%
        )`,
        boxShadow: `
          inset 2px 2px 6px rgba(255,255,255,0.35),
          inset -1px -1px 4px rgba(140,110,50,0.15)
        `
      }}
    >
      {/* Subtle inner ring - minimal, no engravings */}
      <div 
        className="absolute rounded-[50%]"
        style={{
          top: '18%',
          left: '18%',
          right: '18%',
          bottom: '18%',
          border: '1px solid rgba(180,150,80,0.15)',
          background: 'linear-gradient(135deg, rgba(255,255,255,0.08) 0%, transparent 50%)'
        }}
      />
    </div>
  </div>
)

// Clean coin stack - vertical pile
const CoinStack = ({ count, coinSize = 32, className = "" }) => {
  const overlap = coinSize * 0.22 // Smooth overlap
  const totalHeight = count > 0 ? (coinSize * 0.38) + (count - 1) * (coinSize * 0.38 - overlap) : 0
  
  return (
    <div 
      className={cn("relative", className)}
      style={{ 
        width: coinSize,
        height: totalHeight
      }}
    >
      {Array.from({ length: count }).map((_, i) => (
        <div
          key={i}
          className="absolute left-0"
          style={{
            bottom: i * (coinSize * 0.38 - overlap),
            zIndex: i + 1
          }}
        >
          <GoldCoin size={coinSize} shadow={i === count - 1} />
        </div>
      ))}
    </div>
  )
}

// Level 1: Small pile - early start
const Level1Stack = () => (
  <div className="flex items-end justify-center gap-2">
    <CoinStack count={2} coinSize={34} />
    <CoinStack count={1} coinSize={34} />
  </div>
)

// Level 2: Single tall stack - building
const Level2Stack = () => (
  <div className="flex items-end justify-center">
    <CoinStack count={10} coinSize={32} />
  </div>
)

// Level 3: Multiple stacks - making progress
const Level3Stack = () => (
  <div className="flex items-end justify-center gap-2">
    <CoinStack count={5} coinSize={30} />
    <CoinStack count={9} coinSize={30} />
    <CoinStack count={6} coinSize={30} />
  </div>
)

// Level 4: Larger formation - substantial
const Level4Stack = () => (
  <div className="flex items-end justify-center gap-1.5">
    <CoinStack count={4} coinSize={28} />
    <CoinStack count={7} coinSize={28} />
    <CoinStack count={10} coinSize={28} />
    <CoinStack count={7} coinSize={28} />
    <CoinStack count={4} coinSize={28} />
  </div>
)

// Level 5: Premium pyramid - goal achieved
const Level5Stack = () => (
  <div className="flex items-end justify-center gap-1">
    <CoinStack count={3} coinSize={26} />
    <CoinStack count={6} coinSize={26} />
    <CoinStack count={9} coinSize={26} />
    <CoinStack count={11} coinSize={26} />
    <CoinStack count={9} coinSize={26} />
    <CoinStack count={6} coinSize={26} />
    <CoinStack count={3} coinSize={26} />
  </div>
)

export default function InvestmentIndicator({ investmentAmount, totalExpenses, onClick, compact = false }) {
  const { locale, formatCurrency } = useLanguage()
  const isSpanish = locale === 'es-MX'
  const [isVisible, setIsVisible] = useState(false)

  useEffect(() => {
    const timer = setTimeout(() => setIsVisible(true), 100)
    return () => clearTimeout(timer)
  }, [])

  const { level, ratio } = useMemo(() => {
    if (totalExpenses === 0) {
      if (investmentAmount === 0) return { level: INVESTMENT_LEVELS[0], ratio: 0 }
      return { level: INVESTMENT_LEVELS[4], ratio: 1 }
    }
    const r = investmentAmount / totalExpenses
    const lvl = INVESTMENT_LEVELS.find(l => r < l.maxRatio) || INVESTMENT_LEVELS[INVESTMENT_LEVELS.length - 1]
    return { level: lvl, ratio: r }
  }, [investmentAmount, totalExpenses])

  const percentage = Math.round(ratio * 100)

  // Compact coin stack for side-by-side view
  const renderCompactCoinStack = () => {
    const count = Math.min(level.level + 2, 6)
    return (
      <div className="flex items-end gap-0.5">
        {Array.from({ length: count }).map((_, i) => (
          <div
            key={i}
            className="relative"
            style={{
              width: 14,
              height: 14 * 0.38 * (i + 1),
            }}
          >
            {Array.from({ length: i + 1 }).map((_, j) => (
              <div
                key={j}
                className="absolute left-0"
                style={{
                  bottom: j * 3,
                  width: 14,
                  height: 6,
                }}
              >
                <div 
                  className="absolute inset-x-0 bottom-0 rounded-[50%]"
                  style={{
                    height: '100%',
                    background: 'linear-gradient(180deg, #C9A962 0%, #8A6B2E 100%)',
                  }}
                />
                <div 
                  className="absolute inset-x-0 top-0 rounded-[50%]"
                  style={{
                    height: '68%',
                    background: 'linear-gradient(135deg, #E8D9A8 0%, #B09855 100%)',
                  }}
                />
              </div>
            ))}
          </div>
        ))}
      </div>
    )
  }

  if (compact) {
    return (
      <button
        type="button"
        onClick={onClick}
        className={cn(
          "flex flex-col items-center justify-between p-3 rounded-xl w-full",
          "bg-gradient-to-br from-[#FAF6EE] via-[#F5F0E4] to-[#EDE6D6]",
          "border border-[#E5DCC8]/60",
          "hover:border-[#D4C9B0] hover:shadow-md",
          "transition-all cursor-pointer active:scale-[0.98]",
          "focus:outline-none focus:ring-2 focus:ring-[#C9A962]/30",
          isVisible ? "opacity-100" : "opacity-0"
        )}
        data-testid="investment-indicator-btn"
      >
        {/* Top row: Icon + Label */}
        <div className="flex items-center gap-2 w-full mb-1">
          {renderCompactCoinStack()}
          <span className="text-xs font-medium text-[#5C4D35] truncate">
            {isSpanish ? 'Inversión' : 'Investment'}
          </span>
        </div>
        
        {/* Amount */}
        <div className="w-full text-left">
          <span className="text-lg font-bold text-[#4A3D2A]">
            {formatCurrency(investmentAmount)}
          </span>
          <span className="text-xs text-[#8B7355]/70 ml-1">
            ({percentage}%)
          </span>
        </div>
        
        {/* Status message */}
        <p className="text-[10px] text-[#A89878] w-full text-left mt-1">
          {isSpanish ? level.message.es : level.message.en} · {isSpanish ? 'Ver detalles' : 'See details'}
        </p>
      </button>
    )
  }

  const renderCoinStack = () => {
    switch (level.level) {
      case 1: return <Level1Stack />
      case 2: return <Level2Stack />
      case 3: return <Level3Stack />
      case 4: return <Level4Stack />
      case 5: return <Level5Stack />
      default: return <Level1Stack />
    }
  }

  return (
    <button
      type="button"
      onClick={onClick}
      className={cn(
        "relative flex flex-col items-center gap-4 px-6 py-5 rounded-2xl min-w-[180px]",
        // Premium cream/beige gradient background
        "bg-gradient-to-br from-[#FAF6EE] via-[#F5F0E4] to-[#EDE6D6]",
        // Soft border
        "border border-[#E5DCC8]/60",
        // Hover state
        "hover:border-[#D4C9B0] hover:shadow-xl hover:shadow-[#C9B896]/10",
        // Smooth transitions
        "transition-all duration-300 ease-out cursor-pointer",
        "active:scale-[0.98]",
        "focus:outline-none focus:ring-2 focus:ring-[#C9A962]/30",
        // Fade in animation
        isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-3"
      )}
      style={{
        // Soft vignette effect
        boxShadow: isVisible ? `
          inset 0 0 60px rgba(237,230,214,0.5),
          0 4px 20px rgba(150,130,90,0.08),
          0 2px 8px rgba(0,0,0,0.04)
        ` : 'none'
      }}
      data-testid="investment-indicator-btn"
    >
      {/* Subtle level indicator */}
      <div className="absolute top-3 right-4 text-[#B8A070] text-xs font-medium tracking-wide opacity-50">
        Lv.{level.level}
      </div>
      
      {/* Coin visualization area */}
      <div className="min-h-[90px] flex items-end justify-center pt-2 pb-1">
        {renderCoinStack()}
      </div>
      
      {/* Message - clean typography */}
      <p className="text-sm font-semibold text-[#5C4D35] tracking-tight">
        {isSpanish ? level.message.es : level.message.en}
      </p>
      
      {/* Investment stats */}
      <div className="flex flex-col items-center gap-1">
        <span className="text-[11px] text-[#9A8968] font-medium uppercase tracking-wider">
          {isSpanish ? 'Inversión del mes' : 'Monthly investment'}
        </span>
        <span className="text-xl font-bold text-[#4A3D2A] tracking-tight">
          {formatCurrency(investmentAmount)}
        </span>
        <span className="text-[11px] text-[#A89878]">
          {isSpanish ? `${percentage}% de tus gastos` : `${percentage}% of expenses`}
        </span>
      </div>
      
      {/* Subtle CTA */}
      <span className="text-[10px] text-[#B8A888] font-medium tracking-wide">
        {isSpanish ? 'Toca para detalles' : 'Tap for details'}
      </span>
    </button>
  )
}
